/* eslint-disable no-nested-ternary */
import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import { differenceInSeconds, parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import * as AppPropTypes from '../../lib/PropTypes';

import { useThemeClassName, useTheme } from '../../contexts/ThemeContext';
import TriviaButton from '../buttons/TriviaButton';
import QuestionItem from '../items/QuestionItem';
import SportGroupPill from '../items/SportGroupPill';
import SportPeriodLabel from '../partials/SportPeriodLabel';

import styles from '../../../styles/lists/sport-questions.module.scss';

const propTypes = {
    className: PropTypes.string,
    groupsRefs: PropTypes.shape({ current: PropTypes.arrayOf(PropTypes.shape({})) }),
    participation: AppPropTypes.participation,
    game: AppPropTypes.game,
    showAnswers: PropTypes.bool,
    disabled: PropTypes.bool,
    onItemClick: PropTypes.func,
};

const defaultProps = {
    className: null,
    groupsRefs: null,
    participation: null,
    game: null,
    showAnswers: false,
    disabled: false,
    onItemClick: null,
};

function SportQuestionsList({
    className,
    groupsRefs,
    participation,
    game,
    showAnswers,
    disabled,
    onItemClick,
}) {
    const route = useUrlGenerator();
    const { name: theme = null } = useTheme();
    const themeClassName = useThemeClassName(styles);
    const {
        id: participationId = null,
        endedAt: participationEndedAt = null,
        answers = [],
    } = participation || {};
    const participationEnded =
        participationEndedAt !== null &&
        differenceInSeconds(parseISO(participationEndedAt), new Date()) <= 0;
    const { id: gameId, questionsGroups = [], questions: gameQuestions = [] } = game || {};

    const onQuestionClick = useCallback(
        (id) => {
            if (onItemClick !== null) {
                onItemClick(id);
            }
        },
        [onItemClick],
    );

    const finalQuestionsGroups = useMemo(
        () => (questionsGroups.length > 0 ? questionsGroups : [{ questions: gameQuestions }]),
        [questionsGroups, gameQuestions],
    );

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.participationEnded]: participationEnded,
                },
                themeClassName,
                className,
            ])}
        >
            {finalQuestionsGroups.map(
                ({ id, type, label, number, canAnswer, questions = [] }, groupIndex) => {
                    const [firstQuestion] = questions;
                    const count = questions.length;
                    const firstQuestionNum = (questions.length > 0 ? questions[0].index + 1 : 0)
                        .toString()
                        .padStart(2, '0');
                    const lastQuestionNum = (
                        questions.length > 0 ? questions[count - 1].index + 1 : 0
                    )
                        .toString()
                        .padStart(2, '0');

                    return (
                        <div
                            key={`period-${groupIndex + 1}`}
                            className={classNames([
                                styles.period,
                                {
                                    [styles.locked]: !canAnswer,
                                },
                            ])}
                            ref={groupsRefs !== null ? groupsRefs.current[id] : null}
                        >
                            <div className={classNames([styles.periodSection])}>
                                {finalQuestionsGroups.length > 1 ? (
                                    <div className={styles.periodSectionContent}>
                                        <SportGroupPill
                                            className={styles.pill}
                                            iconClassName={styles.pillIcon}
                                            label={label}
                                            unlocked={canAnswer}
                                        />
                                        {participationEnded || !canAnswer ? (
                                            <div className={styles.label}>
                                                <SportPeriodLabel
                                                    number={number}
                                                    ended={participationEnded}
                                                    locked={!canAnswer}
                                                    theme={theme}
                                                />
                                            </div>
                                        ) : null}
                                    </div>
                                ) : null}
                            </div>

                            {type === 'trivia' ? (
                                <div className={classNames([styles.question, styles.isTrivia])}>
                                    <div className={styles.count}>
                                        <span className={styles.countLabel}>
                                            {count > 1 ? (
                                                <FormattedMessage
                                                    defaultMessage="Questions {first}-{last}"
                                                    description="NHL question count label"
                                                    values={{
                                                        first: firstQuestionNum,
                                                        last: lastQuestionNum,
                                                    }}
                                                />
                                            ) : (
                                                <FormattedMessage
                                                    defaultMessage="Question {count}"
                                                    description="NHL question count label"
                                                    values={{
                                                        count: firstQuestionNum,
                                                    }}
                                                />
                                            )}
                                        </span>
                                    </div>
                                    <TriviaButton
                                        url={
                                            participationId !== null
                                                ? route('participation.question', {
                                                      participation: participationId,
                                                      question: firstQuestion?.slug,
                                                  })
                                                : route('game.question', {
                                                      game: gameId,
                                                      question: firstQuestion?.slug,
                                                  })
                                        }
                                        question={firstQuestion}
                                        className={styles.questionContent}
                                        onClick={() => {
                                            onQuestionClick(firstQuestion);
                                        }}
                                    />
                                </div>
                            ) : (
                                <div className={styles.questionsContainer}>
                                    <div className={styles.questions}>
                                        {questions.map((question, questionIndex) => {
                                            const { id: questionId, slug } = question || {};
                                            const answer =
                                                answers.find(
                                                    ({ question: answerQuestionId }) =>
                                                        answerQuestionId === questionId,
                                                ) || null;

                                            const url =
                                                participationId !== null
                                                    ? route('participation.question', {
                                                          participation: participationId,
                                                          question: slug,
                                                      })
                                                    : route('game.question', {
                                                          game: gameId,
                                                          question: slug,
                                                      });

                                            return (
                                                <div
                                                    className={classNames([styles.question, {}])}
                                                    key={`question-${questionIndex + 1}-${slug}`}
                                                >
                                                    <QuestionItem
                                                        className={classNames([
                                                            styles.questionContent,
                                                        ])}
                                                        participationEnded={participationEnded}
                                                        url={url}
                                                        showAnswer={showAnswers}
                                                        question={question}
                                                        answer={answer}
                                                        disabled={disabled}
                                                        onClick={() => {
                                                            onQuestionClick(question);
                                                        }}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                },
            )}
        </div>
    );
}

SportQuestionsList.propTypes = propTypes;
SportQuestionsList.defaultProps = defaultProps;

export default SportQuestionsList;
